import React, { useState } from "react";
import { Form, Input, Select, Button, Alert } from "antd";
import useSousProfiles from "@/sous-profile/hooks/useSousProfiles";

const { Option } = Select;

function SubProfileForm({ parentMember, onCancel, onCreated }) {
  const [form] = Form.useForm();
  const [errorMsg, setErrorMsg] = useState(null);
  const { handleCreateSubprofile, loading } = useSousProfiles({ autoFetch: false });

  const handleFinish = async (values) => {
    // Réinitialiser l'erreur avant chaque tentative
    setErrorMsg(null);

    const result = await handleCreateSubprofile(values, parentMember);

    if (result?.ok) {
      onCreated?.(result.data);
    } else {
      // Afficher l'erreur dans le formulaire (Alert persistante)
      // en complément du toast Ant Design qui disparaît après 3 s
      setErrorMsg(result?.error || "An error occurred. Please try again.");
    }
  };

  const initialProfileType =
    parentMember?.role === "child"
      ? "Child"
      : parentMember?.role === "parent"
      ? "Dependent Parent"
      : "Child";

  return (
    <div style={{ padding: "8px 4px 4px" }}>
      {/* Bannière d'information */}
      <div
        className="subprofile-info-banner"
        style={{
          marginBottom: 20,
          padding: "10px 14px",
          background: "#EBF5FF",
          border: "1px solid #C3DAFE",
          borderRadius: 8,
          color: "#1E3A8A",
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        Create a medical profile for a child or dependent parent that you can
        manage and monitor.
      </div>

      {/* Alert d'erreur persistante — visible si le sous-profil existe déjà */}
      {errorMsg && (
        <Alert
          type="error"
          message={errorMsg}
          showIcon
          closable
          onClose={() => setErrorMsg(null)}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={false}
        initialValues={{ profileType: initialProfileType }}
      >
        <Form.Item label="Profile Type" name="profileType">
          <Select
            size="large"
            style={{ borderRadius: 8 }}
            dropdownStyle={{ borderRadius: 8 }}
          >
            <Option value="Child">Child</Option>
            <Option value="Dependent Parent">Dependent/Parent</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="First Name *"
          name="firstName"
          rules={[
            { required: true, message: "Please enter first name" },
            { whitespace: true, message: "Please enter first name" },
          ]}
        >
          <Input
            size="large"
            placeholder="Enter first name"
            style={{ borderRadius: 8 }}
            // Réinitialise l'erreur dès que l'utilisateur modifie le champ
            onChange={() => errorMsg && setErrorMsg(null)}
          />
        </Form.Item>

        <Form.Item
          label="Last Name *"
          name="lastName"
          rules={[
            { required: true, message: "Please enter last name" },
            { whitespace: true, message: "Please enter last name" },
          ]}
        >
          <Input
            size="large"
            placeholder="Enter last name"
            style={{ borderRadius: 8 }}
            onChange={() => errorMsg && setErrorMsg(null)}
          />
        </Form.Item>

        <Form.Item
          label="Birthdate *"
          name="birthdate"
          rules={[{ required: true, message: "Please enter birthdate" }]}
        >
          <Input size="large" type="date" style={{ borderRadius: 8 }} />
        </Form.Item>

        <Form.Item
          label="Gender *"
          name="gender"
          rules={[{ required: true, message: "Please select gender" }]}
        >
          <Select
            size="large"
            placeholder="Select gender"
            style={{ borderRadius: 8 }}
            dropdownStyle={{ borderRadius: 8 }}
          >
            <Option value="male">Male</Option>
            <Option value="female">Female</Option>
            <Option value="other">Other</Option>
            <Option value="prefer_not_to_say">Prefer not to say</Option>
          </Select>
        </Form.Item>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          <Button onClick={onCancel} size="large" disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            style={{
              background: "#215870",
              borderColor: "#215870",
              borderRadius: 8,
              minWidth: 160,
              fontWeight: 600,
            }}
          >
            Create Subprofile
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default SubProfileForm;